import { useEffect, useState } from "react";
import { useAuthedApi } from "@/api/useAuthedApi";
import type { CompanySummary } from "@/api/companiesApi";

/** Module-level cache: the company name is stable for a session; every
 *  consumer (sidebar popup, greeting subtitle) shares one fetch. */
let cached: CompanySummary | null = null;
let inflight: Promise<CompanySummary | null> | null = null;

/** Test hook — clears the module cache between cases. */
export function _resetCompanyCache(): void {
  cached = null;
  inflight = null;
}

export function useCompany(): CompanySummary | null {
  const { companies } = useAuthedApi();
  const [company, setCompany] = useState<CompanySummary | null>(cached);

  useEffect(() => {
    if (cached || !companies) return;
    inflight ??= companies.me().then(
      (c) => { cached = c; return c; },
      () => {
        // No company / transient error → consumers fall back to generic copy.
        // Clear inflight so the NEXT mount retries instead of pinning the
        // failure for the whole session.
        inflight = null;
        return null;
      },
    );
    let cancelled = false;
    inflight.then((c) => { if (!cancelled && c) setCompany(c); });
    return () => { cancelled = true; };
  }, [companies]);

  return company;
}
